import { Fragment, useState } from 'react';

import type { ResolvedSegment, SegmentKind } from '../lib/promptCompiler';

const TAG_CLASSES: Partial<Record<SegmentKind, string>> = {
  date: 'rounded border border-cyan-300 bg-cyan-100 px-0.5 text-cyan-900',
  section: 'rounded border border-green-300 bg-green-100 px-0.5 text-green-900',
  globalSection: 'rounded border border-teal-300 bg-teal-100 px-0.5 text-teal-900',
  masterProjectAttr: 'rounded border border-blue-300 bg-blue-100 px-0.5 text-blue-900',
  subProjectAttr: 'rounded border border-indigo-300 bg-indigo-100 px-0.5 text-indigo-900',
  subProjects: 'rounded border border-purple-300 bg-purple-100 px-0.5 text-purple-900',
  ifBlock: 'rounded border border-orange-300 bg-orange-100 px-0.5 text-orange-800',
  ifEnd: 'rounded border border-orange-300 bg-orange-100 px-0.5 text-orange-800',
  unresolved: 'rounded border border-red-300 bg-red-100 px-0.5 text-red-900',
};

const KIND_LABELS: Partial<Record<SegmentKind, string>> = {
  date: 'Fecha actual',
  section: 'Sección del proyecto',
  globalSection: 'Sección global',
  masterProjectAttr: 'Atributo master',
  subProjectAttr: 'Atributo subproyecto',
  subProjects: 'Lista de subproyectos',
  unresolved: 'No encontrado',
};

// Splits "some **bold** text" into bold/non-bold fragments
function renderBold(text: string, keyPrefix: string) {
  const parts = text.split(/(\*\*[^*\n]+\*\*)/);
  if (parts.length === 1) return text;
  return parts.map((part, j) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${keyPrefix}-${j}`}>{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={`${keyPrefix}-${j}`}>{part}</Fragment>;
  });
}

interface Props {
  segments: ResolvedSegment[];
}

const PromptPreview = ({ segments }: Props) => {
  const [peeked, setPeeked] = useState<Set<number>>(new Set());

  const toggle = (i: number) =>
    setPeeked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap bg-gray-50 p-4 font-mono text-xs leading-relaxed text-text-default">
      {segments.map((seg, i) => {
        // Headings: same monospace size, but heavier weight + darker color
        if (seg.kind === 'heading1') {
          return (
            <span key={i} className="font-bold text-gray-900">
              {renderBold(seg.text, `h1-${i}`)}
            </span>
          );
        }
        if (seg.kind === 'heading2') {
          return (
            <span key={i} className="font-bold text-gray-800">
              {renderBold(seg.text, `h2-${i}`)}
            </span>
          );
        }
        if (seg.kind === 'heading3') {
          return (
            <span key={i} className="font-bold text-gray-700">
              {renderBold(seg.text, `h3-${i}`)}
            </span>
          );
        }

        // Plain text: only bold parsing, everything else verbatim
        if (seg.kind === 'plain') {
          return <span key={i}>{renderBold(seg.text, `p-${i}`)}</span>;
        }

        // IF markers: non-interactive colored inline labels
        if (seg.kind === 'ifBlock' || seg.kind === 'ifEnd') {
          return (
            <span key={i} className={TAG_CLASSES[seg.kind]}>
              {seg.text}
            </span>
          );
        }

        // Resolved tags: clickable to toggle between resolved value and original tag
        const isPeeking = peeked.has(i);
        const displayText = isPeeking ? seg.token ?? seg.text : seg.text;
        const kindLabel = KIND_LABELS[seg.kind] ?? seg.kind;
        const title = isPeeking
          ? `${kindLabel}${seg.label ? `: ${seg.label}` : ''} — clic para ver valor`
          : `${kindLabel}${seg.label ? `: ${seg.label}` : ''} — clic para ver tag`;

        return (
          <button
            key={i}
            type="button"
            onClick={() => toggle(i)}
            title={title}
            className={`cursor-pointer ${TAG_CLASSES[seg.kind] ?? ''} ${isPeeking ? 'opacity-60' : ''}`}
          >
            {displayText}
          </button>
        );
      })}
    </pre>
  );
};

export default PromptPreview;
